import React, { useState, useEffect } from "react";
import {
  View,
  StyleSheet,
  Text,
  TouchableOpacity,
  Platform,
} from "react-native";

import moment from "moment";
import { find, capitalize } from "lodash";

import Dialog from "../common/dialog";
import ManaButton from "../common/ManaButton";
import RoutinesHelper from "./RoutinesHelper";
import { hasItems, navigate, isWeb, sortBy } from "../../../helpers/utils";
import AppTheme from "../AppTheme";
import ManaDropdown from "../common/dropdown/ManaDropdown";

export default function RoutinesDialog({
  selectedDate,
  timeslots = [],
  controller = () => {},
  applyRoutine = () => {},
  close = () => {},
}) {
  const [routines, setRoutines] = useState([]);
  const [selectedRoutine, setSelectedRoutine] = useState(null);
  const [dayText, setDayText] = useState("");

  const hasRoutines = hasItems(routines);

  useEffect(() => {
    if (!hasItems(controller.state.routines)) return;

    const allRoutines = sortBy(controller.state.routines).map((r) => {
      return {
        ...r,
        display_name:
          r.name +
          " (" +
          capitalize(r.time_interval) +
          (r.is_partial == 1 ? " (Partial)" : "") +
          ")",
      };
    });

    setRoutines(allRoutines);
    setSelectedRoutine(allRoutines[0]);
  }, []);

  useEffect(() => {
    if (!selectedRoutine) return;

    const routine = find(
      controller.state.routines || [],
      (r) => r.id == selectedRoutine.id,
    );
    if (!routine) return;

    const selectedMoment = moment(selectedDate);

    // Current day
    if (routine.time_interval == "day") {
      setDayText(selectedMoment.format("MMM DD"));
      // Current week
    } else {
      const startDate = selectedMoment.startOf("isoWeek");
      const endDate = startDate.clone().add(6, "days");
      setDayText(
        selectedMoment.format("MMM DD") + " - " + endDate.format("MMM DD"),
      );
    }
  }, [selectedRoutine]);

  const createNewRoutine = () => {
    let model = RoutinesHelper.getNewRoutineModel();
    model.timeslots = timeslots;
    model.selectedDate = selectedDate;
    model.hours = JSON.stringify(
      RoutinesHelper.getHoursFromTimeslots(timeslots, selectedDate),
    );

    close();
    navigate(controller, "rn:edit_routine", "changepage", {
      routine: model,
    });
  };

  return (
    <Dialog.Container
      onClose={close}
      showCloseButton
      style={styles.container}
      headerStyle={{ margin: 0 }}
      contentStyle={{
        paddingTop: 0,
        marginTop: 0,
        paddingBottom: 20,
        paddingHorizontal: 21,
      }}
    >
      <View style={styles.header}>
        <Text style={styles.title}>
          {hasRoutines ? "Choose a Routine" : "You have no routines"}
        </Text>
      </View>

      <View
        style={{
          overflow: "visible",
          zIndex: 1000,
        }}
      >
        {hasRoutines ? (
          <ManaDropdown
            options={routines}
            selectedValue={selectedRoutine}
            field="display_name"
            onPickerValueChanged={(m) => {
              setSelectedRoutine(m);
            }}
          />
        ) : null}
      </View>

      <View style={styles.buttons}>
        {hasRoutines ? (
          <ManaButton
            isMainButton
            style={[styles.button]}
            styleText={styles.buttonText}
            text={`Apply to ${dayText}`}
            onPress={() => applyRoutine(selectedRoutine)}
            disabled={!selectedRoutine}
          />
        ) : null}

        <TouchableOpacity
          onPress={createNewRoutine}
          style={styles.createContainer}
        >
          <Text style={styles.routinesLink}>
            {hasRoutines ? "or" : ""} Make Routine from Day
          </Text>
        </TouchableOpacity>
      </View>
    </Dialog.Container>
  );
}

const styles = StyleSheet.create({
  container: {
    alignItems: "center",
    flex: 1,
  },
  picker: {
    borderWidth: 1,
    borderColor: "#000",
    borderRadius: 4,
    height: 40,
  },
  close: {
    position: "absolute",
    top: 5,
    right: 5,
  },
  title: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 14,
    lineHeight: 17,
    letterSpacing: 0.175,
    color: AppTheme.almostBlack,
    marginBottom: 10,
    marginTop: isWeb ? 20 : 0,
  },
  buttons: {
    display: "flex",
    justifyContent: "space-evenly",
    alignItems: "center",
  },
  button: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 286,
    height: 40,
    marginTop: 10,
  },
  buttonText: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 14,
    lineHeight: 17,
    letterSpacing: 0.175,
  },
  separatorContainer: {
    width: "100%",
    position: "relative",
    height: 14,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "row",
    marginTop: 10,
  },
  createContainer: {
    height: 36,
    marginBottom: 11,
  },
  routinesLink: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 12,
    lineHeight: 15,
    letterSpacing: 0.5,
    marginTop: 19,
    color: AppTheme.DEFAULT_COLOR,
  },
});
